"use client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { getSmsRetryAfter, useSmsCooldown } from "@/hooks/use-sms-cooldown";
import { useAuth } from "@/lib/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Clock3, KeyRoundIcon, Loader2Icon, LogIn, MailCheck } from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

// export const metadata: Metadata = {
//   title: "Passwort vergessen",
//   description: "Passwort vergessen",
// };

const Page = () => {
  const [loader, setLoader] = useState(false);
  const [errors, setErrors] = useState<any>(null);
  const [status, setStatus] = useState<any>(null);
  const t = useTranslations("form");
  const { forgotPassword } = useAuth({ middleware: "guest" });
  // Aynı numaraya 3 dakikada 1 SMS kuralı (backend 429 + retry_after döner).
  const smsCooldown = useSmsCooldown();
  const isThrottled = errors?.code === 429;

  const formSchema = z.object({
    email: z
      .string()
      .email({ message: t("error_email") })
      .min(6, { message: t("error_length") }),
    code: z.string().min(1, { message: "Bitte geben Sie einen Code ein" }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      code: "",
    },
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    setLoader(true);
    forgotPassword({ form: values, setLoader, setErrors, setStatus });
  }

  useEffect(() => {
    const retryAfter = getSmsRetryAfter(errors);
    if (retryAfter) smsCooldown.start(retryAfter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  useEffect(() => {
    const storeData = JSON.parse(localStorage.getItem("storeData") ?? "{}");
    if (storeData && typeof storeData === "object" && storeData.code) {
      form.setValue("code", storeData.code ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {loader && (
        <div className="w-full h-screen fixed top-0 left-0 bg-black bg-opacity-50 z-50 flex flex-col items-center justify-center">
          <Loader2Icon className="animate-spin h-10 w-10 text-white" />
        </div>
      )}
      {status ? (
        <>
          <span className="text-success bg-success/10 p-4 rounded-md flex flex-col items-center gap-4">
            <MailCheck size={48} className="flex-shrink-0" />
            <p className="text-center">{t("forgotResponseInfo")}</p>
          </span>

          <div className=" my-5">
            <Link href="/Auth/Login">
              <Button variant={"default"} className="w-full" size={"lg"}>
                <LogIn className="mr-2" />
                {t("loginBtn")}
              </Button>
            </Link>
          </div>
        </>
      ) : (
        <>
          <h1 className="pb-2">{"Bitte füllen Sie die untenstehenden Felder aus und klicken Sie anschließend auf, Passwort vergessen"}</h1>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8" method="post">
              <div className="space-y-2">
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("store_code")}</FormLabel>
                      <FormControl>
                        <Input {...field} autoFocus />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="space-y-2">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("email")}</FormLabel>
                      <FormControl>
                        <Input {...field} autoComplete="email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {errors && isThrottled ? (
                <Alert variant="warning" className="bg-warning/10 text-warning border border-warning/50 [&>svg]:text-warning">
                  <Clock3 className="h-4 w-4" />
                  <AlertTitle>{t("smsThrottleTitle")}</AlertTitle>
                  <AlertDescription>
                    <p>{typeof errors.errors === "string" && errors.errors !== "" ? errors.errors : t("smsThrottleHint")}</p>
                    {smsCooldown.active && <p className="mt-1 font-medium">{t("smsThrottleRetry", { seconds: smsCooldown.seconds })}</p>}
                  </AlertDescription>
                </Alert>
              ) : (
                errors && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>{"message" in errors ? errors.message : t("sww")}</AlertTitle>
                    <AlertDescription>
                      <p>{"errors" in errors && errors.errors !== "" ? errors.errors : t("sww")}</p>
                    </AlertDescription>
                  </Alert>
                )
              )}
              <Button disabled={loader || smsCooldown.active} type="submit" size="lg" className="w-full">
                {smsCooldown.active ? (
                  <>
                    <Clock3 className="mr-2" />
                    {t("smsThrottleRetry", { seconds: smsCooldown.seconds })}
                  </>
                ) : (
                  <>
                    <KeyRoundIcon className="mr-2" />
                    {t("forgotPassword")}
                  </>
                )}
              </Button>
            </form>
          </Form>
        </>
      )}
    </>
  );
};

export default Page;
